import { cleanNumber } from 'pages/Grading/PageProvider';
import { getQuizGradeSnapshot, isQuizCoursework } from 'utils/quizScoring';
import {
    convertCanvasRubric,
    isUnscoredRubric,
    normalizeCompletionStatus,
    normalizeRubricText,
    usesCompleteIncompleteGrading,
} from 'utils/rubricScoring';

export const PushbackType = Object.freeze({
    MISSING: 'missing',
    EXISTING: 'existing',
});

const SemanticGrade = Object.freeze({
    REVIEWED: 'reviewed',
    COMPLETE: 'complete',
    INCOMPLETE: 'incomplete',
    EXCUSED: 'excused',
});

const semanticGradeLabels = Object.freeze({
    [SemanticGrade.REVIEWED]: 'Reviewed',
    [SemanticGrade.COMPLETE]: 'Complete',
    [SemanticGrade.INCOMPLETE]: 'Incomplete',
    [SemanticGrade.EXCUSED]: 'Excused',
});

const getRubricItemTextKey = ({ title, description } = {}) =>
    `${normalizeRubricText(title ?? '')}\u0000${normalizeRubricText(description ?? '')}`;

const buildRubricItemMatches = (localItems, canvasItems, getMatchData) => {
    if (localItems.length !== canvasItems.length) return null;

    const matchesByLocalId = new Map();
    const usedCanvasIds = new Set();
    const saveMatch = (localItem, canvasItem, data) => {
        matchesByLocalId.set(`${localItem.id}`, { localItem, canvasItem, data });
        usedCanvasIds.add(`${canvasItem.id}`);
    };

    for (const localItem of localItems) {
        const exactIdMatch = canvasItems.find(({ id }) => `${id}` === `${localItem.id}`);
        if (!exactIdMatch || usedCanvasIds.has(`${exactIdMatch.id}`)) continue;

        const data = getMatchData(localItem, exactIdMatch);
        if (data !== null) saveMatch(localItem, exactIdMatch, data);
    }

    while (matchesByLocalId.size < localItems.length) {
        let uniqueMatch = null;
        for (const localItem of localItems) {
            if (matchesByLocalId.has(`${localItem.id}`)) continue;

            const candidates = canvasItems
                .filter(({ id }) => !usedCanvasIds.has(`${id}`))
                .map((canvasItem) => ({ canvasItem, data: getMatchData(localItem, canvasItem) }))
                .filter(({ data }) => data !== null);
            if (!candidates.length) return null;
            if (candidates.length === 1) {
                uniqueMatch = { localItem, ...candidates[0] };
                break;
            }
        }

        if (!uniqueMatch) return null;
        saveMatch(uniqueMatch.localItem, uniqueMatch.canvasItem, uniqueMatch.data);
    }

    return localItems.map(({ id }) => matchesByLocalId.get(`${id}`));
};

const buildCanvasOptionIdMap = (localOptions = [], canvasOptions = []) => {
    const matches = buildRubricItemMatches(localOptions, canvasOptions, (localOption, canvasOption) =>
        getRubricItemTextKey(canvasOption) === getRubricItemTextKey(localOption) ? true : null
    );
    if (!matches) return null;

    return Object.fromEntries(matches.map(({ localItem, canvasItem }) => [`${localItem.id}`, `${canvasItem.id}`]));
};

export const buildCanvasRubricPushbackContext = (localAssignment, canvasAssignment) => {
    if (!isUnscoredRubric(localAssignment) || canvasAssignment?.rubric_settings?.hide_points !== true) return null;

    const localCriteria = localAssignment?.rubric?.criteria ?? [];
    const canvasRubric = convertCanvasRubric(canvasAssignment?.rubric, canvasAssignment?.rubric_settings);
    const canvasCriteria = canvasRubric?.criteria ?? [];
    if (!localCriteria.length || localCriteria.length !== canvasCriteria.length) return null;

    const matches = buildRubricItemMatches(localCriteria, canvasCriteria, (localCriterion, canvasCriterion) => {
        if (getRubricItemTextKey(canvasCriterion) !== getRubricItemTextKey(localCriterion)) return null;
        return buildCanvasOptionIdMap(localCriterion.options, canvasCriterion.options);
    });
    if (!matches) return null;

    const criterionIds = Object.fromEntries(
        matches.map(({ localItem, canvasItem }) => [`${localItem.id}`, `${canvasItem.id}`])
    );
    const optionIds = Object.fromEntries(matches.map(({ localItem, data }) => [`${localItem.id}`, data]));

    return {
        apiData: canvasAssignment,
        criterionIds,
        optionIds,
        rubric: canvasRubric,
    };
};

export const remapCanvasRubricSubmission = (submission, context) => {
    const mappedCriteria = {};
    for (const [localCriterionId, localOptionId] of Object.entries(submission?.grading?.criteria ?? {})) {
        const canvasCriterionId = context?.criterionIds?.[localCriterionId];
        const canvasOptionId = context?.optionIds?.[localCriterionId]?.[localOptionId];
        if (!canvasCriterionId || !canvasOptionId) return null;
        mappedCriteria[canvasCriterionId] = canvasOptionId;
    }

    let mappedCriterionFeedback;
    if (submission?.criterion_feedback) {
        mappedCriterionFeedback = {};
        for (const [localCriterionId, feedback] of Object.entries(submission.criterion_feedback)) {
            const canvasCriterionId = context?.criterionIds?.[localCriterionId];
            if (!canvasCriterionId) return null;
            mappedCriterionFeedback[canvasCriterionId] = feedback;
        }
    }

    return {
        ...submission,
        ...(mappedCriterionFeedback && { criterion_feedback: mappedCriterionFeedback }),
        grading: {
            ...submission.grading,
            criteria: mappedCriteria,
        },
    };
};

export const prepareCanvasRubricPushback = (assignment, submission, context) => {
    if (!context) return { assignment, submission };

    const mappedSubmission = remapCanvasRubricSubmission(submission, context);
    if (!mappedSubmission) return null;

    return {
        assignment: {
            ...assignment,
            api_data: {
                ...assignment.api_data,
                ...context.apiData,
            },
            rubric: context.rubric,
        },
        submission: mappedSubmission,
    };
};

const normalizeSemanticGrade = (value) => {
    if (typeof value !== 'string') return null;
    const normalizedValue = value.toLowerCase();
    return semanticGradeLabels[normalizedValue] ? normalizedValue : null;
};

const getRubricRatingsSnapshot = (submission, assignment) =>
    Object.fromEntries(
        (assignment?.rubric?.criteria || []).map(({ id }) => {
            const selectedOptionId = submission?.grading?.criteria?.[id];
            return [`${id}`, selectedOptionId == null ? null : `${selectedOptionId}`];
        })
    );

const haveSameRubricRatings = (currentRatings, pushedRatings) => {
    if (!currentRatings || !pushedRatings) return false;

    const currentEntries = Object.entries(currentRatings);
    const pushedEntries = Object.entries(pushedRatings);
    if (currentEntries.length !== pushedEntries.length) return false;

    return currentEntries.every(([criterionId, optionId]) => pushedRatings[criterionId] === optionId);
};

export const getSnapshotSemanticGrade = (snapshot) =>
    normalizeSemanticGrade(snapshot?.status) ??
    normalizeSemanticGrade(snapshot?.grade) ??
    normalizeSemanticGrade(snapshot?.display_grade);

export const getGradeSnapshotDisplay = (snapshot, fallback = '--') => {
    if (!snapshot) return fallback;

    const semanticGrade = getSnapshotSemanticGrade(snapshot);
    if (semanticGrade) return semanticGradeLabels[semanticGrade];
    if (snapshot.grade != null && snapshot.max_grade != null) {
        return `${cleanNumber(Number(snapshot.grade))}/${cleanNumber(Number(snapshot.max_grade))}`;
    }
    return snapshot.display_grade || fallback;
};

export const getSubmissionGradeSnapshot = (submission, assignment) => {
    if (!submission?.grading || !assignment?.ai_grading_enabled) return null;

    if (isUnscoredRubric(assignment)) {
        const completionStatus = normalizeCompletionStatus(submission.grading.status);
        const grade = usesCompleteIncompleteGrading(assignment)
            ? (completionStatus ?? SemanticGrade.COMPLETE)
            : SemanticGrade.REVIEWED;

        return {
            grade,
            max_grade: null,
            display_grade: semanticGradeLabels[grade],
            rubric_ratings: getRubricRatingsSnapshot(submission, assignment),
        };
    }

    if (isQuizCoursework(assignment, submission)) {
        return getQuizGradeSnapshot(submission, assignment);
    }

    const gradeOverrides = submission.grade_overrides || {};
    const criteria = assignment?.rubric?.criteria || [];
    const score =
        gradeOverrides.total != null
            ? Number(gradeOverrides.total)
            : Object.entries(submission.grading.criteria || {}).reduce((total, [criterionId, optionId]) => {
                  if (gradeOverrides[criterionId] != null) return total + Number(gradeOverrides[criterionId]);
                  const criterion = criteria.find(({ id }) => `${id}` === `${criterionId}`);
                  const option = criterion?.options?.find(({ id }) => `${id}` === `${optionId}`);
                  return total + Number(option?.rate || 0);
              }, 0);
    const maxGrade = criteria.reduce(
        (total, criterion) => total + Math.max(0, ...(criterion.options || []).map(({ rate }) => Number(rate))),
        0
    );

    return {
        grade: score,
        max_grade: maxGrade,
        display_grade: `${score}/${maxGrade}`,
    };
};

export const getSubmissionGradeDisplay = (submission, assignment, fallback = '--') =>
    getGradeSnapshotDisplay(getSubmissionGradeSnapshot(submission, assignment), fallback);

export const getLatestPushbackRecord = (submission) => {
    const history = submission?.grade_pushback_history || [];
    return history.length ? history[history.length - 1] : null;
};

export const getSubmissionLmsSyncStatus = (submission, assignment) => {
    if (!submission?.grading) return null;

    const latest = getLatestPushbackRecord(submission);
    if (!latest) return 'needs_attention';

    const current = getSubmissionGradeSnapshot(submission, assignment);
    if (!current) return null;

    if (
        latest.submission_modified_on != null &&
        Number(submission.modified_on) !== Number(latest.submission_modified_on)
    ) {
        return 'needs_attention';
    }

    if (isUnscoredRubric(assignment)) {
        const currentGrade = getSnapshotSemanticGrade(current);
        const latestGrade = getSnapshotSemanticGrade(latest);
        if (!currentGrade || currentGrade !== latestGrade) return 'needs_attention';
        if (latest.rubric_ratings_pushed === false) return 'synced';
        if (latest.rubric_ratings_pushed !== true) return 'needs_attention';

        return haveSameRubricRatings(current.rubric_ratings, latest.rubric_ratings) ? 'synced' : 'needs_attention';
    }

    return Number(current.grade) === Number(latest.grade) && Number(current.max_grade) === Number(latest.max_grade)
        ? 'synced'
        : 'needs_attention';
};

export const getLmsSubmissionUserId = (submission) =>
    `${process.env.REACT_APP_KIND === 'CANVAS' ? submission.user_id : submission?.Entity?.EntityId}`;

export const isLmsSubmissionGraded = (submission, assignment) =>
    process.env.REACT_APP_KIND === 'CANVAS'
        ? submission?.workflow_state === 'graded' ||
          (isUnscoredRubric(assignment) && Boolean(submission?.rubric_assessment))
        : Boolean(submission?.Feedback?.IsGraded);

export const selectSubmissionsForPushback = (submissions, lmsSubmissions, pushbackType, assignment) => {
    const eligibleUserIds = new Set(
        lmsSubmissions
            .filter((submission) =>
                pushbackType === PushbackType.EXISTING
                    ? isLmsSubmissionGraded(submission, assignment)
                    : !isLmsSubmissionGraded(submission, assignment)
            )
            .map(getLmsSubmissionUserId)
    );

    return submissions.filter((submission) => eligibleUserIds.has(`${submission?.api?.user?.id}`));
};
